/**
 * How to Add a New Filter to FiltersV2
 * ====================================
 *
 * 1. Supported Filter Types
 *    ----------------------
 *    See all supported types in /src/types/filters.ts (FilterType).
 *    Examples:
 *      "text", "number", "date", "datetime", "boolean", "multiwithmodal",
 *      "segment-single", "segments", "tiers", "gender",
 *      "campaign-name-filter", "reward-name-filter", "reward-types-filter", etc.
 *
 * 2. Add Your Filter Configuration
 *    -----------------------------
 *    In your parent component, add a filter object to the `properties` prop.
 *    Required fields: label, query, type (FilterType).
 *    - The `query` field is used as the API parameter name and is injected directly into API calls.
 *    - If your filter supports conditions, the API will receive parameters in the format: `query[condition]`, e.g., `createdAt[lt]`.
 *    Example:
 *      const filters = [
 *        {
 *          label: "Registration Date",
 *          query: "registeredAt",
 *          type: "date",
 *        },
 *        {
 *          label: "Status",
 *          query: "status",
 *          type: "boolean",
 *        }
 *      ];
 *      <FiltersV2 properties={filters} ... />
 *
 * 3. Filters With Conditions (Advanced)
 *    ----------------------------------
 *    If your filter supports conditions (e.g. "greater than", "between", etc.):
 *      - Implement condition logic in /src/components/FiltersV2/Conditions/Conditions.tsx.
 *      - Implement value handling in /src/components/FiltersV2/FilterBase/Values/Values.tsx.
 *    Example:
 *      // In Conditions.tsx, add your filter type to the condition rendering logic.
 *      // In Values.tsx, add your filter type to the value input rendering logic.
 *
 * 4. Export and Validation
 *    ---------------------
 *    - If your filter needs custom export logic, update `parseFiltersForExport` in /src/components/Layout/Export/helpers.ts.
 *    - For custom validation, update `getValidationSchema` in /src/components/FiltersV2/utils.tsx.
 *
 * 5. UI/Behavior Customization (Optional)
 *    ------------------------------------
 *    - For custom filter UI, add logic to /src/components/FiltersV2/FiltersList/ or FilterBase/.
 *
 * Types Reference
 * ---------------
 * - FilterType:      /src/types/filters.ts
 * - Filter:          /src/types/filters.ts
 * - SelectedFilter:  /src/types/filters.ts
 */

import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useRef, useState } from "react";
import { Filter, SelectedFilter } from "types/filters";
import { Total } from "types/listing";
import { Donut } from "./Donut";
import { FiltersList } from "./FiltersList/FiltersList";
import { useStyles } from "./styles";
import { getValidationSchema, pushFilterWithInitialValue } from "./utils";

interface Props {
  "data-test-id"?: string;
  donutLabel?: string;
  properties: Filter[];
  total?: Total;
  savedFilters: SelectedFilter[];
  setFilters: (filters: SelectedFilter[]) => void;
}

export const FiltersV2 = ({
  total,
  donutLabel,
  properties,
  savedFilters,
  setFilters,
}: Props) => {
  const [isEditingFilters, setIsEditingFilters] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const { message } = useMessage();

  const areDonutData =
    typeof total?.all !== "undefined" &&
    total?.all !== 0 &&
    typeof total?.filtered !== "undefined";

  return (
    <div className={classes.filtersContainer}>
      <Formik
        initialValues={{
          filters: savedFilters,
        }}
        enableReinitialize
        onSubmit={async (values) => {
          setFilters(values.filters);
        }}
        validationSchema={getValidationSchema(message)}
        validateOnMount={false}
        validateOnChange={false}
      >
        {({ values, setFieldValue, errors }) => {
          return (
            <>
              <div className={classes.content} ref={anchorRef}>
                <OLMenuButton
                  buttonColor="secondary"
                  buttonVariant="outlined"
                  data-test-id="add-filter"
                  disabled={isEditingFilters}
                  items={properties.map((filter) => ({
                    label: filter.label,
                    onClick: () => {
                      setFieldValue("filters", [
                        ...values.filters,
                        pushFilterWithInitialValue(filter),
                      ]);
                      setIsEditingFilters(true);
                    },
                    isDisabled: !!savedFilters.find(
                      (selectedFilter) => selectedFilter.query === filter.query,
                    ),
                  }))}
                  placeholder={message({ id: "searchAttributes" })}
                  startIcon={<PlusIcon />}
                  submit={message({ id: "addFilter" })}
                />
                {areDonutData && (
                  <Donut total={total} donutLabel={donutLabel} />
                )}
              </div>
              <FiltersList
                anchorRef={anchorRef}
                isOpen={isEditingFilters}
                onClose={() => setIsEditingFilters(false)}
                properties={properties}
                selectedFilters={values.filters}
                isSidebarOpen={isSidebarOpen}
                setSidebarOpen={setSidebarOpen}
                setIsEditingFilters={setIsEditingFilters}
              />
            </>
          );
        }}
      </Formik>
    </div>
  );
};
